import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePMAuth } from '@/contexts/PMAuthContext';
import { pmSupabase as supabase } from '@/integrations/supabase/pmClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Eye, EyeOff, CreditCard, Shield, Mail, Lock } from 'lucide-react';
import { z } from 'zod';

const loginSchema = z.object({
  email: z.string().trim().min(1, 'Email is required').email('Invalid email').max(255),
  password: z.string().min(8, 'Password must be at least 8 characters').max(200),
});

export default function PMLogin() {
  const savedEmail = localStorage.getItem('pm.rememberEmail') || '';
  const savedPassword = localStorage.getItem('pm.rememberPassword') || '';
  const savedRemember = localStorage.getItem('pm.rememberMe') === 'true';
  const [email, setEmail] = useState(savedEmail);
  const [password, setPassword] = useState(savedPassword);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(savedRemember);
  const { signIn, user, pmUser, pmUserLoaded, setAuthGuard } = usePMAuth();
  const navigate = useNavigate();

  // If already logged in and PM user loaded, redirect
  if (user && pmUserLoaded && pmUser) {
    navigate('/pm', { replace: true });
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = loginSchema.safeParse({ email, password });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message || 'Invalid credentials');
      return;
    }
    setError(null);
    setIsLoading(true);

    // Keep auth guard active during entire login flow to prevent
    // other auth contexts from signing out the user
    setAuthGuard(true);

    try {
      const { error: signInError, user: signedInUser } = await signIn(email, password);
      if (signInError) {
        setError(signInError.message || 'Invalid credentials');
        setAuthGuard(false);
        setIsLoading(false);
        return;
      }

      const userId = signedInUser?.id;
      if (!userId) {
        setError('Authentication failed. Please try again.');
        setAuthGuard(false);
        setIsLoading(false);
        return;
      }

      // Wait until the session token is fully propagated before querying RLS-protected tables
      let sessionReady = false;
      for (let i = 0; i < 10; i++) {
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData?.session?.user?.id === userId) { sessionReady = true; break; }
        await new Promise(r => setTimeout(r, 300));
      }
      if (!sessionReady) { setError('Session could not be established. Please try again.'); setAuthGuard(false); setIsLoading(false); return; }

      // Verify PM membership
      const { data: pmMembership, error: pmError } = await (supabase as any)
        .from('pm_portal_users')
        .select('id, status')
        .eq('user_id', userId)
        .eq('status', 'active')
        .maybeSingle();

      if (!pmMembership) {
        setAuthGuard(false);
        await supabase.auth.signOut();
        setError('This account does not have Program Manager access. Contact your administrator.');
        setIsLoading(false);
        return;
      }

      // Remember me — saves email + password; MFA always mandatory regardless
      if (rememberMe) {
        localStorage.setItem('pm.rememberMe', 'true');
        localStorage.setItem('pm.rememberEmail', email);
        localStorage.setItem('pm.rememberPassword', password);
      } else {
        localStorage.removeItem('pm.rememberMe');
        localStorage.removeItem('pm.rememberEmail');
        localStorage.removeItem('pm.rememberPassword');
      }


      setAuthGuard(false);

      // Check MFA
      const { data: mfaData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (mfaData?.nextLevel === 'aal2' && mfaData?.currentLevel !== 'aal2') {
        navigate('/pm/mfa-verify');
      } else {
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const totpFactors = factors?.totp || [];
        if (totpFactors.length === 0) {
          navigate('/pm/mfa-setup');
        } else {
          navigate('/pm');
        }
      }
    } catch (err) {
      setAuthGuard(false);
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-accent/10 mb-2">
            <CreditCard className="w-7 h-7 text-accent" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Program Manager Portal</h1>
          <p className="text-sm text-muted-foreground">
            Sign in to manage your card programs
          </p>
        </div>

        <Card className="border-border/50 bg-card/80 backdrop-blur-sm">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" className="border-destructive/30 bg-destructive/10">
                  <AlertDescription className="text-sm">{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-foreground">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="pl-10 bg-input/50 border-border/50"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-foreground">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-input/50 border-border/50"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="remember"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  className="rounded border-border/50"
                />
                <Label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer">
                  Remember me
                </Label>
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                Sign In
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          This portal is for authorized Program Managers only.
          <br />
          Contact your administrator for access.
        </p>
      </div>
    </div>
  );
}
